"use client";
import React from "react";
import classnames from "classnames";
import Icon from "../../common/components/Icon";
import { useTranslations } from "next-intl";
import useSendContactForm from "../hooks/useSendContactForm";
import { Form, FormField, Select, Textarea } from "../../theme/components/Form";
import { useForm } from "react-hook-form";
import FormErrors from "../../theme/components/FormErrors";
import useFormValidators from "../../theme/hooks/useFormValidators";

const CallBackForm = ({ visible, onHide, className = "call-back-form" }) => {
  const { sendContactForm, data } = useSendContactForm("CALLBACK") as any;
  const t = useTranslations();

  const {
    required,
    validatePhoneNumber,
    validateNotEmpty,
    validateEmail,
    validateDateAfter,
    validateWeekday,
  } = useFormValidators();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    defaultValues: {
      gender: null,
      company: null,
      fullName: null,
      phoneNumber: null,
      emailAddress: null,
      topic: t("topic_general"),
      date: null,
      message: null,
    },
  });

  const onSubmit = async ({
    gender,
    company,
    fullName,
    phoneNumber,
    emailAddress,
    topic,
    date,
    message,
  }) => {
    try {
      await sendContactForm({
        gender,
        company,
        fullName,
        phoneNumber,
        emailAddress,
        topic,
        date,
        message,
      });
      onHide?.();
    } catch (e: any) {
      setError("root.serverError", {
        message: t("error_send_contact_form"),
      });
    }
  };

  if (data?.sendContactForm) {
    return (
      <h2 className="text-center">
        <Icon icon="email-action-check icon--bigger" fill="currentColor" />
        <div>{t("appointment_call_thank_you")}</div>
      </h2>
    );
  }
  return (
    <div className={classnames({ "is-active": visible }, className)}>
      <small className="d-block">{t("appointment_call_label")}</small>
      <h3 className="mt-2 color-secondary">{t("appointment_call_title")}</h3>
      <p className="mb-5">{t("appointment_call_text")}</p>
      <Form
        className="width-100"
        aria-disabled={isSubmitting}
        aria-readonly={isSubmitting}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="row">
          <div className="col-12 col-md-6 mb-4">
            <FormField
              InputField={Select}
              label={t("gender")}
              options={[
                { value: null, label: t("gender") },
                { value: "Herr", label: t("gender_m") },
                { value: "Frau", label: t("gender_f") },
              ]}
              errorMessage={errors.gender?.message as string}
              {...register("gender")}
            />
          </div>
          <div className="col-12 col-md-6 mb-4">
            <FormField
              label={t("company")}
              errorMessage={errors.company?.message as string}
              {...register("company")}
            />
          </div>
          <div className="col-12 mb-4">
            <FormField
              label={t("name")}
              errorMessage={errors.fullName?.message as string}
              {...register("fullName", {
                required,
                validate: validateNotEmpty,
              })}
            />
          </div>
          <div className="col-12 col-md-6 mb-4">
            <FormField
              label={t("telephoneNumber")}
              errorMessage={errors.phoneNumber?.message as string}
              {...register("phoneNumber", {
                required,
                validate: validatePhoneNumber,
              })}
            />
          </div>
          <div className="col-12 col-md-6 mb-4">
            <FormField
              type="email"
              label={t("email_address")}
              errorMessage={errors.emailAddress?.message as string}
              {...register("emailAddress", {
                required,
                validate: validateEmail,
              })}
            />
          </div>
          <div className="col-12 col-md-6 mb-4">
            <FormField
              InputField={Select}
              label={t("topic")}
              errorMessage={errors.topic?.message as string}
              options={[
                {
                  value: "Allgemein",
                  label: t("topic_general"),
                },
                {
                  value: "Inkontinenz",
                  label: t("incontinence"),
                },
                {
                  value: "Stomaversorgung",
                  label: t("ostomy_care"),
                },
                {
                  value: "Tracheostomaversorgung",
                  label: t("tracheo_ostomy_care"),
                },

                {
                  value: "Wundbehandlung",
                  label: t("utilization_wund"),
                },
              ]}
              {...register("topic", { required })}
            />
          </div>

          <div className="col-12 col-md-6 mb-4">
            <FormField
              type="datetime-local"
              label={t("contact_date")}
              errorMessage={errors.date?.message as string}
              min={new Date().toISOString().slice(0, 16)}
              {...register("date", {
                required,
                validate: (v) => {
                  const after = validateDateAfter(new Date())(v);
                  const weekday = validateWeekday(v);
                  if (after !== true) return after;
                  if (weekday !== true) return weekday;
                  return true;
                },
              })}
            />
          </div>
        </div>

        <div>
          <FormField
            rows={3}
            InputField={Textarea}
            label={t("message")}
            errorMessage={errors.message?.message as string}
            {...register("message")}
          />
        </div>

        <FormErrors errors={errors?.root ? { root: errors.root } : null} />

        <div className="mt-4 d-inline-flex justify-content-end w-100">
          <FormField type="submit" value={t("send_request")} />
        </div>
      </Form>
    </div>
  );
};
export default CallBackForm;
